// Info tab: the organizers' Markdown, which admins edit right on the tab

import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import Markdown from './Markdown';
import TextArea from './TextArea';
import Spinner from './Spinner';
import { tournamentInfoSet } from '../utils/firebase';
import { tournamentInfoMaxLength } from '../utils/consts';
import './TournamentInfo.scss';

const validationSchema = new Yup.ObjectSchema({
  info: Yup.string().max(tournamentInfoMaxLength, 'Too long'),
});

// Mounted per edit and seeded once: `text` follows the server and can move on
// while the form is open
function InfoForm({ id, text, onSaved, onClose }) {
  const [seed] = useState(text);

  const formik = useFormik({
    initialValues: { info: seed },
    validationSchema,
    onSubmit: async ({ info }) => {
      const next = info.trim();
      if (next === seed.trim()) return onClose();

      try {
        await tournamentInfoSet({ id, text: next });
        onSaved(next);
      } catch (error) {
        toast.error(error?.message ?? 'Failed to save the info');
      }
    },
  });

  return (
    <form className='tournament-info' onSubmit={formik.handleSubmit}>
      <div className='controls sticky-top'>
        <button className='small bw' type='button' onClick={onClose}>
          Cancel
        </button>
        <button className='small' type='submit' disabled={formik.isSubmitting}>
          Save
        </button>
      </div>
      <div className='source'>
        <TextArea
          name='info'
          placeholder='Add info'
          label='Info, Markdown'
          rows={16}
          maxLength={tournamentInfoMaxLength}
          {...formik.getFieldProps('info')}
          error={
            formik.touched.info && formik.errors.info
              ? formik.errors.info
              : null
          }
        />
        {/* maxLength cuts a paste off silently, so the cap is shown */}
        <div className='info-meta font-12'>
          <span>
            Rules, parking, contacts. Markdown works. Muva Assistant answers
            from this text.
          </span>
          <span className='info-count'>
            {formik.values.info.length} / {tournamentInfoMaxLength}
          </span>
        </div>
      </div>
      <Spinner show={formik.isSubmitting} />
    </form>
  );
}

export default function TournamentInfo({
  id,
  text,
  isSynced,
  isFailed,
  canEdit,
  onEditingChange,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const isOpen = canEdit && isEditing;

  // Lowered on unmount too, or a remount for another tournament leaves the
  // card's tabs locked
  useEffect(() => {
    onEditingChange(isOpen);
    return () => onEditingChange(false);
  }, [isOpen, onEditingChange]);

  // The callable answers before the listener brings the text back, so the
  // saved text stands in until the server's moves off the one it replaced
  const [saved, setSaved] = useState(null);
  if (saved && text !== saved.replaced) setSaved(null);
  const shown = saved ? saved.text : text;

  if (isOpen)
    return (
      <InfoForm
        id={id}
        text={shown}
        onSaved={(newText) => {
          setSaved({ text: newText, replaced: text });
          setIsEditing(false);
        }}
        onClose={() => setIsEditing(false)}
      />
    );

  return (
    <div className='tournament-info'>
      {canEdit && (
        <div className='controls sticky-top'>
          {/* Seeded from the cache, a save could erase a newer text */}
          <button
            className='small bw'
            onClick={() => setIsEditing(true)}
            disabled={!isSynced}
          >
            Edit
          </button>
        </div>
      )}
      {shown ? (
        <Markdown text={shown} />
      ) : isSynced ? (
        <p className='empty'>
          No info yet. Use Edit to add rules, parking and contacts.
        </p>
      ) : isFailed ? (
        <p className='empty'>The info could not be loaded.</p>
      ) : (
        <Spinner show inline />
      )}
    </div>
  );
}
